import { serviceWizardSearchRequestService } from '../services/api/serviceWizardSearchRequestService';
import { getWizardClientSessionId } from './wizardClientSession';

/**
 * Maps a Service Wizard Search Request API row to the wizard shape used by the UI
 * (`location.state.wizard`).
 * @param {object} doc
 * @returns {object | null}
 */
export function mapServiceWizardSearchRequestToWizardState(doc) {
  if (!doc?.id || !doc.serviceType || !doc.formData) return null;
  return {
    serviceType: doc.serviceType,
    subServiceType: doc.subServiceType ?? undefined,
    inquiryKind: doc.inquiryKind ?? 'wizard',
    targetSubServiceType: doc.targetSubServiceType ?? undefined,
    targetServiceTitle: doc.targetServiceTitle ?? undefined,
    formData: doc.formData,
    requestSummary: doc.requestSummary,
  };
}

/**
 * Maps a BusinessLead (e.g. waitlist / empty-match) into the wizard shape used by
 * WizardRequestSummaryDialog. Leads store answers without full formData/_fieldIds.
 * @param {object} lead
 * @returns {object | null}
 */
export function mapBusinessLeadToWizardState(lead) {
  if (!lead?.serviceType) return null;
  const additionalDetails =
    lead.additionalDetails && typeof lead.additionalDetails === 'object'
      ? { ...lead.additionalDetails }
      : {};
  const existingFieldIds =
    additionalDetails._fieldIds && typeof additionalDetails._fieldIds === 'object'
      ? { ...additionalDetails._fieldIds }
      : {};
  additionalDetails._fieldIds = {
    ...existingFieldIds,
    problem: existingFieldIds.problem || lead.subServiceType || '',
    urgency: existingFieldIds.urgency || lead.urgency || '',
  };
  return {
    serviceType: lead.serviceType,
    subServiceType: lead.subServiceType ?? undefined,
    inquiryKind: lead.inquiryKind ?? 'wizard',
    targetSubServiceType: lead.targetSubServiceType ?? undefined,
    targetServiceTitle: lead.targetServiceTitle ?? undefined,
    formData: {
      city: lead.city || '',
      state: lead.state || '',
      urgency: lead.urgency || '',
      photos: Array.isArray(lead.photos) ? lead.photos : [],
      details: lead.details || '',
      additionalDetails,
      address: {
        city: lead.city || '',
        state: lead.state || '',
      },
    },
    requestSummary: lead.requestSummary,
  };
}

/**
 * Move a guest session-owned wizard search request onto the signed-in account.
 * No-op when already linked or the session doc is gone (e.g. global session linker ran first).
 * @param {string} id
 * @param {string} clientSessionId
 */
export async function linkWizardSearchRequestToAccountIfNeeded(id, clientSessionId) {
  const requestId = typeof id === 'string' ? id.trim() : '';
  const sid = typeof clientSessionId === 'string' ? clientSessionId.trim() : '';
  if (!requestId || !sid) return;

  try {
    await serviceWizardSearchRequestService.patchLinkToAccount(requestId, sid);
  } catch {
    /* already linked, wrong session, or not found */
  }
}

/**
 * GET a wizard search request for the current viewer (guest session or authenticated account).
 * @param {string} id
 * @param {{ userLoggedIn?: boolean, clientSessionId?: string | null }} [opts]
 */
export async function fetchWizardSearchRequestForViewer(id, opts = {}) {
  const requestId = typeof id === 'string' ? id.trim() : '';
  if (!requestId) return null;

  const userLoggedIn = Boolean(opts.userLoggedIn);
  const sid =
    (typeof opts.clientSessionId === 'string' && opts.clientSessionId.trim()) ||
    (!userLoggedIn ? getWizardClientSessionId() : null);

  return serviceWizardSearchRequestService.getById(requestId, {
    clientSessionId: sid || undefined,
  });
}

/**
 * After login: associate the guest search with the account, then load it for inquiry UI.
 * @param {string} id
 * @param {string} clientSessionId
 * @returns {Promise<object | null>} wizard state (`location.state.wizard` shape)
 */
export async function linkAndLoadWizardSearchRequest(id, clientSessionId) {
  await linkWizardSearchRequestToAccountIfNeeded(id, clientSessionId);
  const doc = await fetchWizardSearchRequestForViewer(id, { userLoggedIn: true });
  return doc ? mapServiceWizardSearchRequestToWizardState(doc) : null;
}
